import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { motion, useInView } from "framer-motion";
import "react-loading-skeleton/dist/skeleton.css";
import TechnologyModalLayout from "../../../components/Common/TechnologyModalLayout";

const ProjectCards = ({ project, projectId, onEdit, onManageMembers }) => {
  const [techNames, setTechNames] = useState([]);
  const [isDevPopupOpen, setIsDevPopupOpen] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const projectNameKey = project?.name || projectId || "";

  useEffect(() => {
    if (project) {
      const getName = (item) => {
        if (typeof item === "string") return item;
        if (typeof item?.techId === "object" && item.techId?.name) return item.techId.name;
        return item?.name || "";
      };

      const clientFromDetails = Array.isArray(project?.clientTechDetails)
        ? project.clientTechDetails.map((t) => t.name || t.label).filter(Boolean)
        : [];
      const serverFromDetails = Array.isArray(project?.serverTechDetails)
        ? project.serverTechDetails.map((t) => t.name || t.label).filter(Boolean)
        : [];

      const clientFromTech = Array.isArray(project?.technologies?.client)
        ? project.technologies.client.map(getName).filter(Boolean)
        : [];
      const serverFromTech = Array.isArray(project?.technologies?.server)
        ? project.technologies.server.map(getName).filter(Boolean)
        : [];

      const allClient = clientFromDetails.length > 0 ? clientFromDetails : clientFromTech;
      const allServer = serverFromDetails.length > 0 ? serverFromDetails : serverFromTech;

      const combined = Array.from(new Set([...allClient, ...allServer]));
      setTechNames(combined);
    }
  }, [project]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      animate={
        isInView
          ? { opacity: 1, y: 0, scale: 1 }
          : { opacity: 0, y: 30, scale: 0.98 }
      }
      transition={{ duration: 0.2, ease: "easeOut" }}
      layout
      className="w-auto relative"
    >
      <div className="w-auto p-6 border border-slate-200/90 rounded-2xl h-full flex flex-col justify-between bg-white shadow-xs hover:shadow-md transition-all">
        <div>
          <div className="pb-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-1">
              Project Name
            </p>
            {/* <Link
              to={`/projects/${projectNameKey}/overview`}
              className="!no-underline group block"
            > */}
              <h3 className="text-lg font-bold text-slate-900 leading-snug truncate group-hover:text-slate-600 transition-colors">
                {project?.name || "Untitled Project"}
              </h3>
            {/* </Link> */}
          </div>

          <div className="grid grid-cols-2 gap-4 pb-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-1">Project Type</p>
              <p className="text-xs font-semibold text-slate-700">
                {(() => {
                  const plat = project?.platform;
                  if (!plat) return "Web App";
                  const str = Array.isArray(plat) ? plat.join(",") : String(plat);
                  const lower = str.toLowerCase();
                  const hasWeb = lower.includes("web");
                  const hasMobile = lower.includes("mobile");
                  if (hasWeb && hasMobile) return "Web & Mobile App";
                  if (hasWeb) return "Web App";
                  if (hasMobile) return "Mobile App";
                  return plat;
                })()}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-1">Client</p>
              <p className="text-xs font-semibold text-slate-700 truncate">
                {project?.client ? (project.client.name || project.client) : "-"}
              </p>
            </div>
          </div>

          <div className="pb-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-1">
              Timeline
            </p>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span>
                {project?.start_date
                  ? moment(project.start_date).format("MMM D, YYYY")
                  : "-"}
              </span>
              <span className="text-slate-400 font-normal text-sm">-</span>
              <span>
                {project?.end_date
                  ? moment(project.end_date).format("MMM D, YYYY")
                  : "Present"}
              </span>
            </div>
          </div>

          {techNames.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-2">Technologies</p>
              <div className="flex flex-wrap gap-1.5">
                {techNames.map((tech, index) => (
                  <span
                    key={index}
                    className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-slate-100 text-slate-700 border border-slate-200/60"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons: View Details, Edit & Manage Members */}
        <div className="flex items-center gap-2 pt-5 mt-6 border-t border-slate-100">
          {/* <Link
            to={`/projects/${projectNameKey}/overview`}
            className="hidden"
          >
            <span>View Details</span>
          </Link> */}
          <button
            type="button"
            onClick={() => setIsDevPopupOpen(true)}
            className="flex-1 py-2 px-2.5 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors flex items-center justify-center gap-1 shadow-2xs"
          >
            <span>View Details</span>
          </button>
          <button
            type="button"
            onClick={() => onEdit && onEdit(project)}
            className="py-2 px-2.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors flex items-center justify-center gap-1"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => onManageMembers && onManageMembers(project)}
            className="py-2 px-2.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors flex items-center justify-center gap-1"
          >
            Members
          </button>
        </div>
      </div>

      <TechnologyModalLayout
        open={isDevPopupOpen}
        closeModal={() => setIsDevPopupOpen(false)}
      >
        <div className="text-center py-2">
          <h3 className="text-base font-bold text-slate-900 mb-2">
            Coming Soon
          </h3>
          <p className="text-sm text-slate-500">
            This feature is currently under development.
          </p>
        </div>
      </TechnologyModalLayout>
    </motion.div>
  );
};

export default ProjectCards;
